import type { Task } from '../../../../../lib/db/models'

export interface DayBucket {
  date: Date // midnight
  dateKey: string // "YYYY-MM-DD"
  dayOfMonth: number
  tasks: Task[]
}

export interface MonthBucket {
  year: number
  month: number // 0-indexed
  monthKey: string // "YYYY-MM"
  firstDay: Date // midnight on the 1st
  days: DayBucket[] // all days of month, in order; `tasks` may be empty
  hasAnyTasks: boolean
}

/** Effective date used for Upcoming grouping: startDate takes precedence, else dueDate. */
export function effectiveDate(task: Task): Date | null {
  return task.startDate ?? task.dueDate ?? null
}

/** First millisecond of `date`'s day, in local time. */
export function startOfDay(date: Date): Date {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

/** First day of `date`'s month, 00:00 local time. */
export function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

/** Offset `date` by `n` whole months. Preserves day-of-month by clamping. */
export function addMonths(date: Date, n: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + n, 1)
}

/** Number of days in the month containing `date`. */
export function daysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
}

export function monthKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

export function dateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Sort tasks within a single day:
 *  1. startTime asc (nulls last — tasks without a specific time go to the end)
 *  2. order asc (tiebreaker)
 */
export function sortTasksInDay(tasks: Task[]): Task[] {
  return tasks.toSorted((a, b) => {
    const at = a.startTime
    const bt = b.startTime
    if (at !== bt) {
      if (at == null) return 1
      if (bt == null) return -1
      return at.localeCompare(bt)
    }
    return a.order - b.order
  })
}

/** Module-level singleton for empty day-task arrays. Sharing this across
 *  every empty day means React.memo on DayGroup doesn't re-render any day
 *  whose `tasks` was empty before and is empty now (the common case). */
const EMPTY_TASKS: readonly Task[] = Object.freeze([])

/** True when both arrays contain the same task references in the same
 *  order. Cheap O(n) ref-compare — the upstream `useStableEntityArray`
 *  already guarantees that unchanged Tasks keep their object identity. */
function tasksArrayEqual(a: readonly Task[], b: readonly Task[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/**
 * Build month buckets for every month in `[startMonth, endMonth]` inclusive,
 * grouping tasks by their effective date. Every day of every month gets a
 * DayBucket (with empty `tasks` when no task falls on it) so consumers can
 * render a continuous calendar.
 *
 * **Reference stability** (when `prev` is provided): days, day-task arrays,
 * and entire MonthBuckets are reused from `prev` whenever their content is
 * unchanged. This lets `React.memo` on MonthBlock / DayGroup / TaskEntry
 * actually skip work when only one task in one day has changed.
 */
export function buildMonthBuckets(
  tasks: Task[],
  startMonth: Date,
  endMonth: Date,
  prev?: MonthBucket[],
): MonthBucket[] {
  // Pre-group tasks by dateKey for O(n) lookup per day.
  const tasksByDateKey = new Map<string, Task[]>()
  for (const task of tasks) {
    const d = effectiveDate(task)
    if (!d) continue
    const key = dateKey(startOfDay(d))
    const list = tasksByDateKey.get(key)
    if (list) list.push(task)
    else tasksByDateKey.set(key, [task])
  }

  // Build a flat lookup of prev DayBuckets so unchanged days can be reused
  // even when months have been reordered or partially replaced.
  const prevDaysByKey = new Map<string, DayBucket>()
  const prevMonthsByKey = new Map<string, MonthBucket>()
  if (prev) {
    for (const m of prev) {
      prevMonthsByKey.set(m.monthKey, m)
      for (const d of m.days) prevDaysByKey.set(d.dateKey, d)
    }
  }

  const months: MonthBucket[] = []
  const start = startOfMonth(startMonth)
  const end = startOfMonth(endMonth)

  let cursor = start
  // Guard against inverted ranges — yield nothing.
  if (cursor > end) return months

  while (cursor <= end) {
    const year = cursor.getFullYear()
    const month = cursor.getMonth()
    const numDays = daysInMonth(cursor)
    const mKey = monthKey(cursor)
    const prevMonth = prevMonthsByKey.get(mKey)

    const days: DayBucket[] = new Array(numDays)
    let hasAnyTasks = false
    let allDaysReused = prevMonth !== undefined && prevMonth.days.length === numDays

    for (let d = 1; d <= numDays; d++) {
      const dKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      const dayTasks = tasksByDateKey.get(dKey)
      const sorted = dayTasks ? sortTasksInDay(dayTasks) : EMPTY_TASKS as Task[]
      if (sorted.length > 0) hasAnyTasks = true

      const prevDay = prevDaysByKey.get(dKey)
      if (prevDay && tasksArrayEqual(prevDay.tasks, sorted)) {
        days[d - 1] = prevDay
      } else {
        days[d - 1] = {
          date: new Date(year, month, d),
          dateKey: dKey,
          dayOfMonth: d,
          tasks: sorted,
        }
        allDaysReused = false
      }
    }

    if (
      prevMonth &&
      allDaysReused &&
      prevMonth.hasAnyTasks === hasAnyTasks
    ) {
      months.push(prevMonth)
    } else {
      months.push({
        year,
        month,
        monthKey: mKey,
        firstDay: prevMonth?.firstDay ?? cursor,
        days,
        hasAnyTasks,
      })
    }

    cursor = addMonths(cursor, 1)
  }

  // If every MonthBucket was reused from prev, return the prev outer array
  // too. Otherwise downstream `useMemo`s that depend on `months` (e.g.
  // `flatTaskIds`) would still recompute and produce new callback refs,
  // which propagates through MonthBlock → DayGroup as a "prop changed"
  // re-render across the entire page.
  if (
    prev &&
    months.length === prev.length &&
    months.every((m, i) => m === prev[i])
  ) {
    return prev
  }

  return months
}

/**
 * Filter tasks for Upcoming rendering:
 *  - not soft-deleted
 *  - not paused (ruhend) — those live in the Ruhend view
 *  - has an effective date (startDate or dueDate)
 *  - belongs to current life space (via projectId ∈ lifeSpaceProjectIds, OR orphan for personal)
 *  - not completed — except ids in `graceIds` (kept visible for ~2s after toggle)
 */
export function filterUpcomingTasks({
  tasks,
  lifeSpace,
  lifeSpaceProjectIds,
  graceIds,
}: {
  tasks: Task[]
  lifeSpace: 'personal' | 'work' | 'home'
  lifeSpaceProjectIds: Set<string>
  graceIds: Set<string>
}): Task[] {
  return tasks.filter(task => {
    if (task.deletedAt) return false
    if (task.pausedAt != null) return false
    if (!effectiveDate(task)) return false

    // Orphan tasks (no project) surface only in their own lifespace, read
    // from `task.lifeSpace` (required since PR 1 of the lifeSpace migration).
    // Before PR 1 the convention was "orphans = personal only"; after PR 2
    // Kurz-notiert is the primary orphan source per lifespace.
    if (task.projectId == null) {
      if (task.lifeSpace !== lifeSpace) return false
    } else if (!lifeSpaceProjectIds.has(task.projectId)) {
      return false
    }

    if (task.status === 'completed' && !graceIds.has(task.id)) return false

    return true
  })
}
